import { useQuery } from '@tanstack/react-query';
import { Link, useLocation } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { MotionButton, cn } from '@iheart/ui';
import { api } from '../lib/api';
import { useCurrentWorkspace } from '../lib/workspace';

/**
 * Full-screen paywall shown when a workspace's subscription is paused (trial
 * ended or payment lapsed). Blocks the app until they subscribe — but always
 * lets them reach Settings → Billing to pay. Comped/internal workspaces are
 * never paused, so this never shows for them.
 */
export function PaywallGate() {
  const ws = useCurrentWorkspace();
  const loc = useLocation();

  const billing = useQuery({
    queryKey: ['billing', ws?.publicId],
    queryFn: () =>
      api<{ subscription: { status: string; planLabel: string } }>(
        `/workspaces/${ws!.publicId}/billing`,
      ),
    enabled: !!ws,
    staleTime: 60_000,
  });

  const status = billing.data?.subscription?.status;
  const onBilling = loc.pathname.startsWith('/settings/billing');
  const isClosed = status === 'cancelled';
  if ((status !== 'paused' && !isClosed) || onBilling) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-white/85 p-6 backdrop-blur-xl">
      <div className="max-w-md rounded-2xl border border-white/40 bg-white p-8 text-center shadow-2xl">
        <div
          className={cn(
            'mx-auto mb-4 flex size-12 items-center justify-center rounded-full',
            isClosed ? 'bg-red-100' : 'bg-pink-100',
          )}
        >
          <Lock className={cn('size-6', isClosed ? 'text-red-600' : 'text-pink-600')} />
        </div>
        <h2 className="text-lg font-bold text-neutral-800">
          {isClosed ? 'Your account has closed' : 'Your plan is paused'}
        </h2>
        <p className="mt-2 text-sm leading-relaxed text-neutral-500">
          {isClosed ? (
            <>
              This account has been cancelled and shut down. Any previous content, media and
              connections have been (or will shortly be) deleted. Sign up to a paid plan to start
              again with a fresh, brand-new account.
            </>
          ) : (
            <>
              Your free trial has ended (or a payment didn't go through). Subscribe to pick up right
              where you left off — all your data is safe and waiting.
            </>
          )}
        </p>
        <Link to="/settings/billing" className="mt-6 block">
          <MotionButton size="lg" className="w-full">
            {isClosed ? 'Sign up to a paid plan' : 'Choose a plan & pay'}
          </MotionButton>
        </Link>
      </div>
    </div>
  );
}
